import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import {
  WORKFLOW_FILTER_FIELDS,
  DATE_VALUE_OPTIONS,
  createDefaultFilter,
  validateFilter,
} from '../../utils/workflowFilterUtils';

const OPERATOR_LABELS = {
  contains: 'Contains',
  exists: 'Exists',
  is: 'Is',
  is_any_of: 'Is any of',
};

function FilterRow({ filter, index, onChange, onRemove, errors }) {
  const fieldConfig = WORKFLOW_FILTER_FIELDS.find((f) => f.value === filter.field);
  const isDate = fieldConfig?.type === 'date';
  const operators = isDate
    ? ['exists', ...DATE_VALUE_OPTIONS]
    : (fieldConfig?.operators || []);
  const showValue = filter.operator && filter.operator !== 'exists';
  const showEndDate = isDate && filter.operator === 'Between';

  return (
    <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3" data-testid={`filter-row-${index}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-zinc-500">
          {index > 0 ? filter.conjunction : 'Filter'}
        </span>
        <button
          type="button"
          className="text-xs text-zinc-500 hover:text-red-400"
          onClick={() => onRemove(filter.id)}
        >
          Remove
        </button>
      </div>

      <Select
        value={filter.field}
        onValueChange={(value) => onChange(filter.id, { field: value, operator: '', value: '', valueEnd: '' })}
      >
        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid={`filter-field-${index}`}>
          <SelectValue placeholder="Select field" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-700">
          {WORKFLOW_FILTER_FIELDS.map((field) => (
            <SelectItem key={field.value} value={field.value} className="text-zinc-200">
              {field.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {fieldConfig ? (
        <Select
          value={filter.operator}
          onValueChange={(value) => onChange(filter.id, { operator: value, value: '', valueEnd: '' })}
        >
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid={`filter-operator-${index}`}>
            <SelectValue placeholder="Select operator" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            {operators.map((op) => (
              <SelectItem key={op} value={op} className="text-zinc-200">
                {OPERATOR_LABELS[op] || op}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      {showValue && fieldConfig?.type === 'select' ? (
        <Select value={filter.value} onValueChange={(value) => onChange(filter.id, { value })}>
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid={`filter-value-${index}`}>
            <SelectValue placeholder="Select value" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            {fieldConfig.options.map((opt) => (
              <SelectItem key={opt} value={opt} className="text-zinc-200 capitalize">
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      {showValue && fieldConfig?.type === 'text' ? (
        <div>
          <Input
            value={filter.value}
            onChange={(e) => onChange(filter.id, { value: e.target.value })}
            placeholder="Enter value"
            className="bg-zinc-800 border-zinc-700 text-white"
            data-testid={`filter-value-${index}`}
          />
          {errors[filter.id] ? (
            <p className="mt-1 text-xs text-red-400">{errors[filter.id]}</p>
          ) : null}
        </div>
      ) : null}

      {showValue && isDate && !['Today', 'Tomorrow', 'Yesterday'].includes(filter.operator) ? (
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-zinc-400">
              {showEndDate ? 'Start Date' : 'Date'}
            </Label>
            <Input
              type="date"
              value={filter.value}
              onChange={(e) => onChange(filter.id, { value: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid={`filter-date-value-${index}`}
            />
          </div>
          {showEndDate ? (
            <div>
              <Label className="text-xs text-zinc-400">End Date</Label>
              <Input
                type="date"
                value={filter.valueEnd}
                onChange={(e) => onChange(filter.id, { valueEnd: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid={`filter-date-end-${index}`}
              />
            </div>
          ) : null}
          {errors[filter.id] ? (
            <p className="text-xs text-red-400">{errors[filter.id]}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const WorkflowAdvancedFiltersDrawer = ({
  open,
  onClose,
  filters,
  onChangeFilters,
  onApply,
}) => {
  const [draftFilters, setDraftFilters] = useState(filters);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setDraftFilters(filters.length ? filters : [createDefaultFilter()]);
      setErrors({});
    }
  }, [open, filters]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const updateFilter = (id, patch) => {
    setDraftFilters((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const addFilter = (conjunction = 'AND') => {
    setDraftFilters((prev) => [...prev, { ...createDefaultFilter(), conjunction }]);
  };

  const removeFilter = (id) => {
    setDraftFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleApply = () => {
    const nextErrors = {};
    draftFilters.forEach((filter) => {
      const err = validateFilter(filter);
      if (err) nextErrors[filter.id] = err;
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onChangeFilters(draftFilters);
    onApply?.(draftFilters);
    onClose?.();
  };

  const handleClear = () => {
    setDraftFilters([createDefaultFilter()]);
    setErrors({});
    onChangeFilters([]);
    onApply?.([]);
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        data-testid="workflow-filters-backdrop"
        onClick={onClose}
        role="presentation"
      />
      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-zinc-800 bg-zinc-950 shadow-2xl"
        data-testid="workflow-advanced-filters-drawer"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Advanced filters</h2>
            <p className="text-sm text-zinc-500">Refine your workflow list</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-800 hover:text-white"
            data-testid="workflow-filters-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
          {draftFilters.map((filter, index) => (
            <FilterRow
              key={filter.id}
              filter={filter}
              index={index}
              onChange={updateFilter}
              onRemove={removeFilter}
              errors={errors}
            />
          ))}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300"
              data-testid="workflow-filter-add"
              onClick={() => addFilter('AND')}
            >
              <Plus size={14} className="mr-1" />
              Add filter
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300"
              data-testid="workflow-filter-add-and"
              onClick={() => addFilter('AND')}
            >
              Add AND
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-800 px-5 py-4">
          <Button
            type="button"
            variant="ghost"
            className="text-zinc-400 hover:text-white"
            data-testid="workflow-filter-clear"
            onClick={handleClear}
          >
            Clear all filters
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-zinc-700 text-zinc-300"
              data-testid="workflow-filter-cancel"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="workflow-filter-apply"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default WorkflowAdvancedFiltersDrawer;
